import { createUseStyles } from "react-jss";
import {
    inputBackgroundColor,
    controlBackgroundColor,
    alpha,
    brighten,
    darken,
    borderColor,
    foregroundColor,
    backgroundless,
} from "../../theme/Mixins";

function getBorderRadius(theme) {
    return theme.ControlAlpha === 0 && theme.BorderWidth === 0
        ? 0
        : Math.min(theme.BorderRadius, theme.InputHeight / 2);
}

export default createUseStyles((theme: any) => ({
    select: {
        backgroundColor: ({ autoComplete }) =>
            autoComplete
                ? inputBackgroundColor(theme)
                : alpha(theme.ControlBackground, theme.ControlAlpha),
        border: [[theme.BorderWidth, "solid", theme.ControlBorderColor]],
        borderBottom:
            theme.ControlAlpha === 0 && theme.BorderWidth === 0
                ? [[3, "solid", theme.ControlBorderColor]]
                : undefined,
        borderRadius: getBorderRadius(theme),
        boxSizing: "border-box",
        color: theme.ForegroundColorDark,
        composes: "select",
        display: "inline-block",
        fontSize: ({ size }) => size === "XSmall" && 13,
        height: theme.InputHeight + theme.BorderWidth * 2,
        lineHeight: `${theme.InputHeight}px`,
        position: "relative",
        transition: "color 200ms, background-color 200ms, box-shadow 200ms, border-color 200ms",
        verticalAlign: "middle",
        width: "100%",
        "& div": {
            cursor: "default",
        },
    },
    selectExpanded: {
        borderRadius: [[getBorderRadius(theme), getBorderRadius(theme), 0, 0]],
        borderBottom: [
            [
                theme.ControlAlpha === 0 && theme.BorderWidth === 0 ? 3 : 0,
                "solid",
                theme.ControlBorderColor,
            ],
        ],
        composes: "select--expanded",
    },
    selectFocused: {
        backgroundColor: ({ autoComplete }) =>
            autoComplete
                ? inputBackgroundColor(theme, true)
                : alpha(
                      darken(theme.ControlBackground, theme.ActiveHighlightValue),
                      theme.ControlAlpha,
                  ),
        borderColor: borderColor(theme, theme.PrimaryColor),
        composes: "select--focused",
    },
    selectDisabled: {
        composes: "select__disabled",
        opacity: 0.5,
    },
    selectContent: {
        background: "transparent",
        border: 0,
        boxSizing: "border-box",
        color: "inherit",
        composes: "select__content",
        display: "block",
        fontFamily: "inherit",
        fontSize: "inherit",
        height: "100%",
        lineHeight: "inherit",
        outline: "none",
        overflow: "hidden",
        padding: ({ icon, size, disabled }) =>
            size === "XSmall"
                ? [[0, disabled ? 16 : 24, 0, icon ? 32 : 16]]
                : [[0, disabled ? 16 : 38, 0, icon ? 42 : 16]],
        resize: "none",
        textAlign: "left",
        textOverflow: "ellipsis",
        transition: "background-color 200ms, border-color 200ms",
        WebkitAppearance: "none",
        whiteSpace: "nowrap",
        width: "100%",
    },
    option: {
        alignItems: "center",
        composes: "select__option",
        cursor: "default",
        display: "flex",
        minHeight: theme.OptionHeight,
        outline: "none",
        padding: [[0, 16]],
        transition: "background-color 200ms, color 200ms",
        userSelect: "none",
        color: foregroundColor(theme, theme.ControlBackground),
        "&:hover": {
            backgroundColor: darken("#fff", theme.ActiveHighlightValue),
        },
        "&:focus": {
            background: theme.PrimaryColor,
            color: foregroundColor(theme, theme.PrimaryColor),
            "&:hover": {
                backgroundColor: darken(theme.PrimaryColor, theme.ActiveHighlightValue),
            },
        },
        "&:active": {
            color: foregroundColor(theme, theme.PrimaryColor),
            backgroundColor: theme.PrimaryColor,
        },
    },
    optionNonInteractive: {
        alignItems: "center",
        composes: "select__option",
        cursor: "default",
        display: "flex",
        minHeight: theme.OptionHeight,
        outline: "none",
        padding: [[0, 16]],
        userSelect: "none",
        color: brighten(foregroundColor(theme, controlBackgroundColor(theme)), 2),
    },
    message: {
        alignItems: "center",
        backgroundColor: theme.ControlBackground,
        composes: "select__message",
        display: "flex",
        fontSize: "0.8em",
        minHeight: theme.OptionHeight,
        outline: "none",
        padding: [[0, 16]],
    },
    listbox: {
        background: "#FFF",
        listStyleType: "none",
        composes: "select__listbox",
        margin: 0,
        maxHeight: theme.OptionHeight * 8,
        outline: "none",
        overflowY: "auto",
        padding: 0,
        position: "-webkit-sticky" /* Safari */,
    },
    listboxExpanded: {
        composes: "select__listbox--expanded",
        borderBottomLeftRadius: Math.min(theme.BorderRadius, theme.InputHeight / 2),
        borderBottomRightRadius: Math.min(theme.BorderRadius, theme.InputHeight / 2),
        position: "relative",
    },
    dropDown: {
        boxSizing: "border-box",
        composes: "select__listbox--drop-down",
        position: "sticky",
        marginTop: "auto",
        zIndex: 10,
        border: [[1, "solid", theme.ControlBorderColor]],
    },
    check: {
        composes: "select__check",
        marginLeft: 12,
        transition: "opacity 200ms",
    },
    hidden: {
        composes: "select__listbox--hidden",
        display: "none",
    },
    invisible: {
        opacity: 0,
    },
    icon: {
        composes: "input-box__accessory",
        position: "absolute",
        top: 0,
        bottom: 0,
        fontSize: ({ size }) => size === "XSmall" && 13,
        margin: "auto",
        pointerEvents: "none",
    },
    iconLeft: {
        color: brighten(theme.ForegroundColorDark, 1.6),
        composes: "input-box__accessory-left",
        left: 0,
        marginLeft: ({ size }) => (size === "XSmall" ? 8 : 16),
    },
    iconCaret: {
        composes: "input-box__accessory-right",
        right: 0,
        marginRight: ({ size }) => (size === "XSmall" ? 8 : 16),
    },
    choiceActive: {
        composes: "choice--active",
        background: ({ selected }) => {
            if (backgroundless(theme)) {
                return undefined;
            }
            return selected
                ? darken(theme.PrimaryColor, theme.ActiveHighlightValue)
                : darken(theme.ControlBackground, theme.ActiveBackgroundValue);
        },
        color: ({ selected }) => {
            return selected
                ? foregroundColor(theme, darken(theme.PrimaryColor, theme.ActiveHighlightValue))
                : foregroundColor(
                      theme,
                      darken(theme.ControlBackground, theme.ActiveBackgroundValue),
                  );
        },
        outlineColor: () => backgroundless(theme) && theme.PrimaryColor,
    },
}));
